import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import CategoryBar from '../components/CategoryBar';
import SeriesCard from '../components/SeriesCard';
import { Loader2 } from 'lucide-react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const BACKEND_URL = '';  // Use relative URLs
const API = '/api';

const ComicsPage = () => {
  useDocumentTitle('Comics');
  const [selectedCategory, setSelectedCategory] = useState('all-comics');
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSeries();
  }, [selectedCategory]);

  const fetchSeries = async () => {
    setLoading(true);
    try {
      let url = `${API}/series?limit=50`;
      
      // Filter by category if not "all-comics" or "daily"
      if (selectedCategory !== 'all-comics' && selectedCategory !== 'daily') {
        url += `&genre=${selectedCategory}`;
      }
      
      // For "daily", fetch recent updates
      if (selectedCategory === 'daily') {
        url = `${API}/homepage/daily-updates`;
      }

      const response = await axios.get(url);
      
      // Handle different response structures
      const seriesData = response.data.items || response.data.series || response.data;
      setSeries(Array.isArray(seriesData) ? seriesData : []);
    } catch (error) {
      console.error('Error fetching series:', error);
      setSeries([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Category Bar */}
      <CategoryBar
        type="comic"
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
      />

      {/* Content */}
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-8">
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-black text-white mb-2">
            {selectedCategory === 'daily' ? 'Daily Updates' : 
             selectedCategory === 'all-comics' ? 'All Comics' :
             selectedCategory.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
          </h1>
          <p className="text-gray-400">
            Discover amazing stories from independent creators
          </p>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          </div>
        ) : series.length === 0 ? (
          /* Empty State */
          <div className="text-center py-20">
            <div className="text-6xl mb-4">📚</div>
            <h2 className="text-2xl font-bold text-white mb-2">No Comics Yet</h2>
            <p className="text-gray-400 mb-6">Be the first to publish in this category!</p>
            <Link 
              to="/create-series" 
              className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-lg transition-colors"
            >
              Create Your Comic
            </Link>
          </div>
        ) : (
          /* Series Grid */
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-6">
            {series.map((item) => (
              <SeriesCard key={item.id} series={item} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ComicsPage;
